/**
 * Company Navigation Utility
 * Helper functions for navigating between company-specific dashboard pages
 */

export interface CompanyNavigationInfo {
    currentPage: string;
    previousPage: string | null;
    nextPage: string | null;
    baseRoute: string;
    companyId: string;
}

/**
 * Get navigation info for current company page
 */
export const getCompanyNavigationInfo = (currentRoute: string): CompanyNavigationInfo | null => {
    // Remove query parameters
    const routeWithoutQuery = currentRoute.split('?')[0];

    // Extract company ID and page type
    // Handle routes like /dashboard/companies/{id}/users
    const companyMatch = routeWithoutQuery.match(/\/dashboard\/companies\/([^/]+)(?:\/([^/]+))?/);

    if (!companyMatch) {
        return null;
    }

    const companyId = companyMatch[1];
    const page = companyMatch[2] || 'details'; // Default to basic company dashboard if no sub-page

    // Determine base route
    const baseRoute = `/dashboard/companies/${companyId}`;

    // Define page order matching getDynamicSubItems.ts
    const pages = ['users', 'products', 'product-details', 'suppliers', 'user-rights'];

    const currentIndex = pages.indexOf(page);

    // If we're on the main company dashboard, currentIndex might be -1
    // We can consider the main dashboard as the starting point or handle pages only
    if (currentIndex === -1) {
        // If we're on the main dashboard, maybe 'users' is the next page?
        if (page === 'details') {
            return {
                currentPage: 'details',
                previousPage: null,
                nextPage: 'users',
                baseRoute,
                companyId
            };
        }
        return null;
    }

    const previousPage = currentIndex > 0 ? pages[currentIndex - 1] : null;
    const nextPage = currentIndex < pages.length - 1 ? pages[currentIndex + 1] : null;

    return {
        currentPage: page,
        previousPage,
        nextPage,
        baseRoute: `/dashboard/companies/${companyId}`,
        companyId
    };
};

/**
 * Get route for a specific company page
 */
export const getCompanyPageRoute = (baseRoute: string, page: string): string => {
    return `${baseRoute}/${page}`;
};

/**
 * Get display name for a page
 */
export const getCompanyPageName = (page: string): string => {
    const pageNames: Record<string, string> = {
        'users': 'Company Users',
        'suppliers': 'Company Suppliers',
        'products': 'Company Products',
        'product-details': 'Product Details',
        'user-rights': 'User Rights',
        'details': 'Company Dashboard'
    };
    return pageNames[page] || page;
};
